import * as questionnaires from '@seen/core/models/questionnaires/index'
import { ValidatedTool } from '@seen/core/models/validatedTool'
import { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import { recordKeys } from '@seen/core/utils'
import type { JSX } from 'react'
import { z } from 'zod'
import { FormSubmitButton } from '#src/components/core/form/FormSubmitButton.tsx'
import { useValidatedToolForm } from '#src/components/validated-tool-form/shared/useValidatedToolForm.ts'
import { ValidatedToolField } from '#src/components/validated-tool-form/shared/ValidatedToolField.tsx'
import {
  ValidatedToolInnerForm,
  type ValidatedToolInnerFormProps,
} from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import { ValidatedToolSection } from '#src/components/validated-tool-form/shared/ValidatedToolSection.tsx'
import type {
  ValidatedToolFieldSuggestion,
  ValidatedToolSetValue,
} from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'
import type { UseFormReturn } from '#src/hooks/useForm.ts'

export interface MIIFormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  response?: ValidatedToolResponse
  /* AI-staged answers to pre-populate an unsubmitted form. */
  prefillValuesByLinkId?: Record<string, string>
  /* Per-field AI suggestion overlay, keyed by linkId. */
  renderFieldSuggestions?: (
    setValue: ValidatedToolSetValue
  ) => Record<string, ValidatedToolFieldSuggestion>
  restorationId?: string
}

const schema = z.object({
  'physical-activity': z.string().nullable(),
  'lifting-light-objects': z.string().nullable(),
  'walking-light-exercise': z.string().nullable(),
  'wait-empty-bladder': z.string().nullable(),
  'sudden-urge': z.string().nullable(),
  'reach-bathroom': z.string().nullable(),
  'protection-type': z.string().nullable(),
  'protection-frequency': z.string().nullable(),
  'activity-changes': z.string().nullable(),
  'social-impact': z.string().nullable(),
})

const validatedTool = new ValidatedTool(questionnaires.miiQuestionnaire)

export function MIIForm({
  response,
  prefillValuesByLinkId,
  renderFieldSuggestions,
  restorationId,
  ...delegated
}: MIIFormProps): JSX.Element {
  const form = useValidatedToolForm({
    schema,
    validatedTool,
    response,
    prefillValuesByLinkId,
    restorationId,
  })
  const fieldSuggestions = renderFieldSuggestions?.(form.setSuggestionValue)

  return (
    <ValidatedToolInnerForm
      validatedTool={validatedTool}
      subtitle="For determining level of urinary incontinence"
      form={form}
      response={response}
      {...delegated}
    >
      <ValidatedToolSection
        title="Michigan Incontinence Index"
        subtitle="For each question, please choose the response that best describes your voiding habits during the past month."
      >
        {recordKeys(schema.shape).map((linkId) => (
          <ValidatedToolField
            key={linkId}
            control={form.control}
            name={linkId}
            validatedTool={validatedTool}
            suggestion={fieldSuggestions?.[linkId]}
          />
        ))}
      </ValidatedToolSection>

      <Score form={form} validatedTool={validatedTool} response={response} />

      {!response && (
        <div>
          <FormSubmitButton control={form.control}>Submit</FormSubmitButton>
        </div>
      )}
    </ValidatedToolInnerForm>
  )
}

function Score({
  form,
  validatedTool,
  response,
}: {
  form: UseFormReturn<any>
  validatedTool: ValidatedTool
  response?: ValidatedToolResponse
}): JSX.Element {
  const valuesByLinkId = form.watch()
  const result = response
    ? response
    : ValidatedToolResponse.buildSafe(validatedTool, { valuesByLinkId })
  return (
    <div className="flex flex-col gap-2">
      <h2>
        Score: <b>{result.score}</b>
      </h2>
    </div>
  )
}
